import type { TranslationType } from './en';

export const es: TranslationType = {
  days: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
  months: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
  pageTitle: 'DOMINION | Perfil de Riesgo',
  title: 'Informe de Perfil de Riesgo',
  profileTitle: 'Tu Perfil de Riesgo:',
  allocationTitle: 'Distribución Recomendada de Activos',
  recommendationsTitle: 'Recomendaciones de Inversión:',
  recommendationText: 'de tu cartera',
  footer: 'Este es un documento generado por el sistema. Solo para uso interno.',
  website: 'www.dominion-cs.com',
  signature: {
    title: 'Firma y Aceptación',
    clientName: 'Nombre del Cliente:',
    signature: 'Firma:',
    date: 'Fecha:',
    advisorName: 'Nombre del Asesor:',
  },
  header: {
    poweredBy: "Powered by DOMINION"
  },
  welcome: {
    title: "Bienvenido al Perfil de Riesgo",
    description: "Este cuestionario te ayudará a determinar tu perfil de riesgo como inversor y te proporcionará una recomendación personalizada sobre cómo distribuir tus inversiones.",
    whatWeEvaluate: "¿Qué evaluaremos?",
    items: [
      "Tu horizonte temporal de inversión",
      "Tu experiencia en los mercados financieros",
      "Tu tolerancia a la volatilidad",
      "Tus objetivos de inversión"
    ],
    duration: "El cuestionario toma aproximadamente 3-5 minutos en completarse.",
    keyboardHint: "Presiona {enter} para comenzar"
  },
  questions: [
    {
      id: 'q1',
      question: '¿Cuál es tu horizonte temporal de inversión?',
      options: [
        { value: '10', label: '1-3 años', description: 'Plazo medio-corto' },
        { value: '15', label: '3-5 años', description: 'Plazo medio' },
        { value: '20', label: '5-10 años', description: 'Plazo medio-largo' },
        { value: '25', label: 'Más de 10 años', description: 'Plazo largo, sin prisa' },
      ]
    },
    {
      id: 'q2',
      question: '¿Cuál es tu experiencia en inversiones?',
      options: [
        { value: '0', label: 'Ninguna', description: 'Soy nuevo en esto' },
        { value: '5', label: 'Básica', description: 'Algunas inversiones simples' },
        { value: '10', label: 'Intermedia', description: 'Varios años invirtiendo' },
        { value: '15', label: 'Avanzada', description: 'Amplia experiencia en mercados' },
        { value: '20', label: 'Experta', description: 'Profesional o muy experimentado' },
      ]
    },
    {
      id: 'q3',
      question: 'Si tu inversión bajara un 20% en un mes, ¿qué harías?',
      options: [
        { value: '0', label: 'Vendería todo inmediatamente', description: 'No puedo soportar las pérdidas' },
        { value: '5', label: 'Vendería la mayor parte', description: 'Me pondría muy nervioso' },
        { value: '10', label: 'Esperaría a ver qué pasa', description: 'Analizaría la situación' },
        { value: '15', label: 'Mantendría mi posición', description: 'Confío en mi estrategia' },
        { value: '20', label: 'Compraría más', description: 'Es una oportunidad' },
      ]
    },
    {
      id: 'q4',
      question: '¿Qué porcentaje de tus ahorros destinarás a inversión?',
      options: [
        { value: '20', label: 'Menos del 10%', description: 'Solo una pequeña parte' },
        { value: '15', label: '10-25%', description: 'Una porción moderada' },
        { value: '10', label: '25-50%', description: 'Una parte significativa' },
        { value: '5', label: '50-75%', description: 'La mayor parte' },
        { value: '0', label: 'Más del 75%', description: 'Casi todos mis ahorros' },
      ]
    },
    {
      id: 'q5',
      question: '¿Cuál es tu objetivo principal de inversión?',
      options: [
        { value: '0', label: 'Preservar capital', description: 'No quiero perder dinero' },
        { value: '5', label: 'Generar ingresos estables', description: 'Crecimiento sostenido por encima de la inflación' },
        { value: '10', label: 'Crecimiento moderado', description: 'Balance entre seguridad y rentabilidad' },
        { value: '15', label: 'Crecimiento significativo', description: 'Maximizar ganancias con riesgo controlado' },
        { value: '20', label: 'Máxima rentabilidad', description: 'Busco los mayores retornos posibles' },
      ]
    },
    {
      id: 'q6',
      question: '¿Cómo reaccionas ante la volatilidad del mercado?',
      options: [
        { value: '0', label: 'Me estresa mucho', description: 'Pierdo el sueño por ello' },
        { value: '5', label: 'Me preocupa bastante', description: 'Reviso constantemente mis inversiones' },
        { value: '10', label: 'Me inquieta un poco', description: 'La monitoreo pero sin obsesionarme' },
        { value: '15', label: 'La acepto como normal', description: 'Forma parte del proceso' },
        { value: '20', label: 'La veo como oportunidad', description: 'Me permite encontrar mejores precios' },
      ]
    },
  ],
  questionCard: {
    keyboardHint: "Presiona {numbers} para seleccionar o {enter} para continuar"
  },
  riskProfiles: {
    conservative: {
      profile: 'Conservador',
      description: 'Priorizas la seguridad y estabilidad de tu capital. Prefieres minimizar riesgos aunque esto signifique menores rendimientos potenciales.',
      scoreRange: '0-20',
      ranges: [
        { category: 'Renta Fija', min: 70, max: 100, color: '#5955FF' },
        { category: 'Renta Variable', min: 0, max: 30, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 10, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 0, color: '#FF6B6B' },
      ],
      funds: {
        'Renta Fija': [
          { name: 'DCS Global Bonds', composition: '100% Renta Fija' },
          { name: 'DCS Cash', composition: '100% Renta Fija' },
          { name: 'DCS G10 Macro Rates', composition: '100% Renta Fija' },
        ],
        'Renta Variable': [
          { name: 'DCS Cautious', composition: '20% Renta Variable + 80% Renta Fija' },
          { name: 'DCS Balanced', composition: '60% Renta Variable + 40% Renta Fija' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Renta Variable + 40% Renta Fija' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
      }
    },
    moderateConservative: {
      profile: 'Moderado Conservador',
      description: 'Buscas un equilibrio inclinado hacia la seguridad, pero estás dispuesto a asumir algo de riesgo para obtener mejores rendimientos.',
      scoreRange: '21-40',
      ranges: [
        { category: 'Renta Fija', min: 50, max: 70, color: '#5955FF' },
        { category: 'Renta Variable', min: 20, max: 50, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 15, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 5, color: '#FF6B6B' },
      ],
      funds: {
        'Renta Fija': [
          { name: 'DCS Global Bonds', composition: '100% Renta Fija' },
          { name: 'DCS Cash', composition: '100% Renta Fija' },
          { name: 'DCS G10 Macro Rates', composition: '100% Renta Fija' },
        ],
        'Renta Variable': [
          { name: 'DCS Cautious', composition: '20% Renta Variable + 80% Renta Fija' },
          { name: 'DCS Balanced', composition: '60% Renta Variable + 40% Renta Fija' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Renta Variable + 40% Renta Fija' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Renta Variable' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Renta Variable' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    moderate: {
      profile: 'Moderado',
      description: 'Buscas un balance equilibrado entre riesgo y rentabilidad. Entiendes que la volatilidad es parte de obtener mejores rendimientos a largo plazo.',
      scoreRange: '41-60',
      ranges: [
        { category: 'Renta Fija', min: 30, max: 50, color: '#5955FF' },
        { category: 'Renta Variable', min: 40, max: 80, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 15, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 10, color: '#FF6B6B' },
      ],
      funds: {
        'Renta Fija': [
          { name: 'DCS Global Bonds', composition: '100% Renta Fija' },
          { name: 'DCS Cash', composition: '100% Renta Fija' },
          { name: 'DCS G10 Macro Rates', composition: '100% Renta Fija' },
        ],
        'Renta Variable': [
          { name: 'DCS Balanced', composition: '60% Renta Variable + 40% Renta Fija' },
          { name: 'DCS Aggressive', composition: '80% Renta Variable + 20% Renta Fija' },
          { name: 'DCS Multi Asset Sustainable - Balanced', composition: '60% Renta Variable + 40% Renta Fija' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Renta Variable' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Renta Variable' },
          { name: 'DCS Emerging Market Equities', composition: '100% Renta Variable' },
          { name: 'DCS New Technologies', composition: '100% Renta Variable' },
          { name: 'DCS Global Growth', composition: '100% Renta Variable' },
          { name: 'DCS Managed Fund', composition: '100% Renta Variable' },
          { name: 'DCS North American Opportunities Fund', composition: '100% Renta Variable' },
          { name: 'Nasdaq Tracker fund', composition: '100% Renta Variable' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    moderateAggressive: {
      profile: 'Moderado Agresivo',
      description: 'Estás dispuesto a asumir riesgos significativos en busca de mayores rendimientos. Toleras la volatilidad y tienes un horizonte de inversión a largo plazo.',
      scoreRange: '61-80',
      ranges: [
        { category: 'Renta Fija', min: 10, max: 30, color: '#5955FF' },
        { category: 'Renta Variable', min: 60, max: 90, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 20, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 15, color: '#FF6B6B' },
      ],
      funds: {
        'Renta Fija': [
          { name: 'DCS Global Bonds', composition: '100% Renta Fija' },
          { name: 'DCS G10 Macro Rates', composition: '100% Renta Fija' },
        ],
        'Renta Variable': [
          { name: 'DCS Aggressive', composition: '80% Renta Variable + 20% Renta Fija' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Renta Variable' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Renta Variable' },
          { name: 'DCS Emerging Market Equities', composition: '100% Renta Variable' },
          { name: 'DCS New Technologies', composition: '100% Renta Variable' },
          { name: 'DCS Global Growth', composition: '100% Renta Variable' },
          { name: 'DCS Managed Fund', composition: '100% Renta Variable' },
          { name: 'DCS North American Opportunities Fund', composition: '100% Renta Variable' },
          { name: 'Nasdaq Tracker fund', composition: '100% Renta Variable' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    },
    aggressive: {
      profile: 'Agresivo',
      description: 'Buscas maximizar tus rendimientos y estás dispuesto a asumir altos niveles de riesgo. Tienes experiencia en inversiones y un horizonte temporal largo.',
      scoreRange: '81-120',
      ranges: [
        { category: 'Renta Fija', min: 0, max: 10, color: '#5955FF' },
        { category: 'Renta Variable', min: 70, max: 100, color: '#90d977' },
        { category: 'Alternativos', min: 0, max: 20, color: '#D9B877' },
        { category: 'Crypto', min: 0, max: 20, color: '#FF6B6B' },
      ],
      funds: {
        'Renta Fija': [
          { name: 'DCS Global Bonds', composition: '100% Renta Fija' },
        ],
        'Renta Variable': [
          { name: 'DCS Aggressive', composition: '80% Renta Variable + 20% Renta Fija' },
          { name: 'DCS MSCI All World Tracker Fund', composition: '100% Renta Variable' },
          { name: 'DCS S&P 500 Tracker', composition: '100% Renta Variable' },
          { name: 'DCS Emerging Market Equities', composition: '100% Renta Variable' },
          { name: 'DCS New Technologies', composition: '100% Renta Variable' },
          { name: 'DCS Global Growth', composition: '100% Renta Variable' },
          { name: 'DCS Managed Fund', composition: '100% Renta Variable' },
          { name: 'Opportunities Plus', composition: '100% Renta Variable' },
          { name: 'DCS North American Opportunities Fund', composition: '100% Renta Variable' },
          { name: 'Nasdaq Tracker fund', composition: '100% Renta Variable' },
          { name: 'DCS Nifty Fifty Tracker Fund', composition: '100% Renta Variable' },
        ],
        'Alternativos': [
          { name: 'DCS Physical Gold Tracker Fund', composition: '100% Alternativos' },
        ],
        'Crypto': [
          { name: 'Bitcoin Tracker Fund', composition: '100% Crypto' },
        ],
      }
    }
  },
  results: {
    title: "Tu perfil de riesgo:",
    chartTitle: "Distribución Recomendada de Activos",
    recommendationsTitle: "Rangos Recomendados",
    fundsTitle: "Fondos Sugeridos",
    recommendationText: "de tu cartera",
    rangeText: "Rango:",
    rangeLabel: "Rango",
    totalAllocation: "Asignación Total del Portfolio",
    noteTitle: "Nota:",
    noteText: "Este perfil es orientativo. Te recomendamos consultar con un asesor financiero antes de tomar decisiones de inversión.",
    downloadPDF: "Descargar PDF",
    restartTest: "Realizar Test Nuevamente",
    preparingPDF: "Preparando tu PDF...",
    pleaseWait: "Por favor espera un momento"
  },
  clientInfoModal: {
    title: "Información del Cliente",
    clientNameLabel: "Nombre Completo del Cliente",
    clientNamePlaceholder: "Juan Pérez",
    advisorNameLabel: "Nombre Completo del Asesor",
    advisorNamePlaceholder: "María García",
    cancelButton: "Cancelar",
    confirmButton: "Generar PDF"
  },
  navigation: {
    start: "Comenzar",
    next: "Siguiente",
    previous: "Anterior"
  },
  loading: {
    generatingResults: "Generando tus resultados"
  },
  admin: {
    login: {
      title: "Panel de Administración",
      subtitle: "Gestión de Perfiles de Riesgo",
      emailPlaceholder: "Correo Electrónico",
      passwordPlaceholder: "Contraseña",
      loginButton: "Iniciar Sesión",
      invalidCredentials: "Usuario o contraseña incorrectos"
    },
    dashboard: {
      title: "Historial de Perfiles de Riesgo",
      subtitle: "Visualiza y gestiona todos los perfiles de riesgo generados",
      logoutButton: "Cerrar Sesión",
      searchPlaceholder: "Buscar por nombre de cliente o asesor...",
      filterByProfile: "Filtrar por perfil",
      filterByLanguage: "Filtrar por idioma",
      allProfiles: "Todos los Perfiles",
      allLanguages: "Todos los Idiomas",
      tableHeaders: {
        date: "Fecha",
        client: "Cliente",
        advisor: "Asesor",
        score: "Puntuación",
        profile: "Perfil",
        language: "Idioma",
        actions: "Acciones"
      },
      actions: {
        view: "Ver",
        download: "Descargar",
        delete: "Eliminar"
      },
      noResults: "No se encontraron perfiles de riesgo",
      noResultsDescription: "Intenta ajustar tu búsqueda o filtros",
      totalResults: "Total: {count} perfiles",
      confirmDelete: "¿Estás seguro que quieres eliminar este perfil de riesgo?",
      deleteSuccess: "Perfil de riesgo eliminado exitosamente",
      deleteCancel: "Cancelar"
    }
  }
};